import React, { useMemo, useRef, useState } from 'react'
import { Checkbox, Dropdown } from 'antd'
import { CaretDown } from '@phosphor-icons/react'
import { SearchOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import CustomModal from 'components/global/CustomModal'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import { StatusList } from '../../../../Tabs/Attendances/Modals/CheckInOut/constants'
import LeagueParticipants from './LeagueParticipants'
import 'components/global/styles/styles.scss'
import '../../../../Tabs/Attendances/styles/checkin-out.scss'

const LeagueCheckInOutModal: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [statusDropOpen, setStatusDropOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const {
    checkInOutModalOpen,
    setCheckInOutModalOpen,
    selectedDate,
    setSelectedDate,
  } = leagueAttendanceStore(store => ({
    checkInOutModalOpen: store.checkInOutModalOpen,
    setCheckInOutModalOpen: store.setCheckInOutModalOpen,
    selectedDate: store.selectedDate,
    setSelectedDate: store.setSelectedDate,
  }))

  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''

  const dateStr = useMemo(() => {
    const d = new Date(selectedDate)
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
  }, [selectedDate])

  const closeModal = () => {
    setSelectedDate(new Date())
    setCheckInOutModalOpen(false)
  }

  const handleSearch = debounce((val: string) => {
    setSearchQuery(val)
  }, 300)

  const filteredStatusList = StatusList.filter(
    (item: any) => item.value !== 'pending',
  )

  const statusDropItems = [
    {
      key: 'checkboxes',
      label: (
        <Checkbox.Group
          onChange={(vals: any) => setSelectedStatuses(vals)}
          value={selectedStatuses}
        >
          {filteredStatusList.map((item: any) => (
            <div key={item.key} className="flex mt-15px align-center">
              <Checkbox value={item.value} />
              <div
                style={{ backgroundColor: item.color }}
                className="flex px-15px py-10px radius-12 ml-10px"
              >
                <img src={item.img} alt={item.title} />
                <h4 className="ml-10px text-12 w-600">{item.title}</h4>
              </div>
            </div>
          ))}
        </Checkbox.Group>
      ),
    },
    {
      key: 'actions',
      label: (
        <div className="flex justify-between mx-15px">
          <button
            className="clr-btn"
            onClick={() => {
              setSelectedStatuses([])
              setStatusDropOpen(false)
            }}
          >
            Clear
          </button>
          <button
            className="aply-btn"
            onClick={() => setStatusDropOpen(false)}
          >
            Apply
          </button>
        </div>
      ),
    },
  ]

  return (
    <CustomModal
      openModal={checkInOutModalOpen}
      isCloseModalCustom
      closeModal={closeModal}
      title="Check-in/out"
      width={1200}
      footer={false}
    >
      <div>
        {/* Header row — search + status filter */}
        <div className="flex justify-between my-20px">
          <div className="flex align-center">
            <div className="search-input-box w-fit">
              <input
                type="search"
                className="search-input-group"
                placeholder="Search participants"
                style={{ width: '320px' }}
                onChange={e => {
                  e.preventDefault()
                  handleSearch(e.target.value)
                }}
              />
              <SearchOutlined />
            </div>

            <div ref={dropdownRef} className="flex align-center">
              <Dropdown
                menu={{ items: statusDropItems }}
                placement="bottomLeft"
                arrow={false}
                className="mr-10px"
                open={statusDropOpen}
                onOpenChange={setStatusDropOpen}
                overlayStyle={{ width: '300px' }}
              >
                <button
                  className={`${statusDropOpen ? 'filter-btn-click' : 'filter-btn'} ${
                    selectedStatuses.length ? 'applied' : ''
                  } w-500`}
                  onClick={() => setStatusDropOpen(v => !v)}
                >
                  Status <CaretDown />
                </button>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Body — participants list + date calendar */}
        <div className="flex justify-between gap-100px">
          <div className="flex-1 overflow-auto">
            <LeagueParticipants
              leagueId={leagueId}
              date={dateStr}
              searchQuery={searchQuery}
              statusFilter={selectedStatuses}
            />
          </div>

          <div>
            <div className="schedule-calendar">
              <Calendar
                calendarType="US"
                onChange={(date: Date) => setSelectedDate(date)}
                value={selectedDate}
              />
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  )
}

export default LeagueCheckInOutModal
